import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { OVERLAY_MICRO_LABEL_CLASS } from "./HubStylesPanel";

export type FilterDropdownOption = {
  value: string;
  label: string;
};

type FilterDropdownProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: FilterDropdownOption[];
  testId?: string;
};

const PANEL_MAX_HEIGHT = 280;
const PANEL_Z_INDEX = 60;

export function FilterDropdown({
  label,
  value,
  onChange,
  options,
  testId,
}: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label ?? value;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panelWidth = Math.max(rect.width, 168);
    let top = rect.bottom + 4;
    let left = rect.left;

    if (top + PANEL_MAX_HEIGHT > window.innerHeight - 8) {
      const aboveTop = rect.top - 4 - PANEL_MAX_HEIGHT;
      if (aboveTop >= 8) top = aboveTop;
    }

    if (left + panelWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - panelWidth - 8);
    }

    setPanelStyle({
      position: "fixed",
      top,
      left,
      width: panelWidth,
      maxHeight: PANEL_MAX_HEIGHT,
      zIndex: PANEL_Z_INDEX,
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const selectOption = useCallback(
    (optValue: string) => {
      onChange(optValue);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [onChange],
  );

  const moveHighlight = useCallback(
    (delta: number) => {
      setHighlightIndex((prev) => {
        const next = prev + delta;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
    },
    [options.length],
  );

  const handleListboxKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        const opt = options[highlightIndex];
        if (opt) selectOption(opt.value);
      }
    },
    [close, highlightIndex, moveHighlight, options, selectOption],
  );

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen((prev) => !prev);
        return;
      }
      if (!open) {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          setOpen(true);
        }
        return;
      }
      handleListboxKeyDown(event);
    },
    [handleListboxKeyDown, open],
  );

  useEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex((opt) => opt.value === value);
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
    updatePosition();
    requestAnimationFrame(() => panelRef.current?.focus());
  }, [open, options, updatePosition, value]);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscapeCapture = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener("keydown", handleEscapeCapture, true);
    return () => {
      document.removeEventListener("keydown", handleEscapeCapture, true);
    };
  }, [close, open]);

  useEffect(() => {
    if (!open || highlightIndex < 0) return;
    panelRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${highlightIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, open]);

  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      <span className={OVERLAY_MICRO_LABEL_CLASS}>{label}</span>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        className="inline-flex h-8 min-w-0 max-w-[128px] items-center gap-1 rounded-md border border-white/10 bg-bgNested pl-2 pr-2 text-[12px] font-medium text-white/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#38bdf8]/55 focus:ring-offset-0"
      >
        <span className="min-w-0 flex-1 truncate text-left">{selectedLabel}</span>
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 text-white/35 transition-transform duration-150",
            open && "rotate-180",
          )}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              highlightIndex >= 0
                ? `${listboxId}-option-${highlightIndex}`
                : undefined
            }
            onKeyDown={handleListboxKeyDown}
            style={panelStyle}
            className="overflow-y-auto rounded-[10px] border border-white/10 bg-bgSurface py-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)] stronom-scroll"
            data-testid={testId ? `${testId}-panel` : undefined}
          >
            {options.map((opt, index) => {
              const selected = opt.value === value;
              const highlighted = highlightIndex === index;
              return (
                <div
                  key={opt.value}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={selected}
                  data-option-index={index}
                  onClick={() => selectOption(opt.value)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-[12px] font-medium transition-colors",
                    selected ? "text-[#5cc8f5]" : "text-white/80",
                    (highlighted || selected) &&
                      "bg-[rgba(56,189,248,0.14)]",
                    !selected && "hover:bg-[rgba(56,189,248,0.14)] hover:text-white",
                  )}
                >
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {selected ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    ) : null}
                  </span>
                  <span className="min-w-0 truncate">{opt.label}</span>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
